import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  Dialog,
  DialogContent,
} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ImageIcon from '@mui/icons-material/Image'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import DeleteIcon from '@mui/icons-material/Delete'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import Print from '@mui/icons-material/Print'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { postMPDataWithParams } from 'src/mentor/services'
import { DocumentEditorDialog } from 'src/consumer/components/Helpers/DocumentEditor'
import { getDataWithParams } from 'src/consumer/services/profileService'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import EmrImagesUploadDialog from 'src/clinic/components/ClinicManageBookingsPage/emrImagesUpload/EmrImagesUploadDialog'
import { getPrescriptionApis } from 'src/shared/utils/calendarReportApiHelper'
import { PORTAL_TYPES, detectCurrentPortal } from 'src/shared/config/calendarReportApiConfig'

// Module-level state to persist dialog open state across component unmounts
// Key is eventId to support multiple instances
const dialogStateByEventId = {}

const StyledCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.15);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`

const ThumbnailContainer = styled(Box)`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`

const Thumbnail = styled(Box)`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid rgba(16, 185, 129, 0.2);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: rgba(16, 185, 129, 0.5);
    transform: scale(1.05);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ImageItem = styled(Box)`
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(16, 185, 129, 0.1);
  position: relative;
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`

const DeleteOverlay = styled(Box)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
`

const ThumbnailWrapper = styled(Box)`
  position: relative;
  display: inline-block;
`

const ThumbnailDeleteButton = styled(IconButton)`
  position: absolute;
  top: -8px;
  right: -8px;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  padding: 2px;
  z-index: 10;
  
  &:hover {
    background: rgba(239, 68, 68, 1);
  }
  
  svg {
    font-size: 14px;
  }
`

// Styled components for delete confirmation dialog
const DeleteDialogContent = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  text-align: center;
`

const DeleteDialogIcon = styled(Box)`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  
  svg {
    font-size: 32px;
    color: #ef4444;
  }
`

const DeleteDialogTitle = styled(Typography)`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
`

const DeleteDialogDescription = styled(Typography)`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 24px;
  max-width: 280px;
`

const DeleteDialogActions = styled(Box)`
  display: flex;
  gap: 12px;
  width: 100%;
`

const CancelButton = styled(Button)`
  flex: 1;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  text-transform: none;
  background: #f3f4f6;
  color: #374151;
  border: none;
  
  &:hover {
    background: #e5e7eb;
  }
`

const DeleteButton = styled(Button)`
  flex: 1;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  text-transform: none;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.35);
  
  &:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    box-shadow: 0 6px 16px rgba(239, 68, 68, 0.45);
  }
  
  &:disabled {
    background: #fca5a5;
    box-shadow: none;
  }
`

export default function PrescriptionDocumentsUploadSection({ 
  eventId, 
  bookedEventInfo, 
  prescriptionData: prescriptionDataProp, 
  setPrescriptionData,
  mentorProfileData, 
  onImagesUpdate,
  emrSectionsToShowInOrder,
  section,
}) {
  // Track whether we have already completed a successful API fetch so we don't
  // re-fetch every time the parent prescription context re-renders (which would
  // also call setPrescriptionData and create a re-render cascade).
  const hasFetchedRef = useRef(false)

  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [images, setImages] = useState([])
  const [signedUrls, setSignedUrls] = useState([])
  const [error, setError] = useState(null)
  // Simple dialog state
  const [uploadDialogOpenState, setUploadDialogOpenInternal] = useState(false)
  
  const uploadDialogOpen = uploadDialogOpenState

  // Stable setter - no deps needed, React state setters are stable references
  const setUploadDialogOpen = setUploadDialogOpenInternal
  
  const [deletingIndex, setDeletingIndex] = useState(null)
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, index: null, url: null })
  const [documentEditorState, setDocumentEditorState] = useState({ open: false, index: null, url: null })

  // Handle both data structures:
  // - prescriptionDataProp (from context) has nested prescriptionData.prescriptionData.uploadedImages
  // - Or it could be the inner data directly with uploadedImages at top level
  const prescriptionData = useMemo(() => {
    // If it has nested prescriptionData, use that
    if (prescriptionDataProp?.prescriptionData?.uploadedImages !== undefined) {
      return prescriptionDataProp.prescriptionData
    }
    // Otherwise use the prop directly (in case it's already the inner data)
    return prescriptionDataProp
  }, [prescriptionDataProp])

  // Helper function to convert image ID to media URL
  const getMediaUrl = (imageId) => {
    if (!imageId) return ''
    // If it's already a full URL, return as is
    if (imageId.startsWith('http://') || imageId.startsWith('https://')) {
      return imageId
    }
    // Otherwise, convert image ID to media URL
    return `https://media.aumhum.com/images/prod/${imageId}`
  }

  // Determine if we're in mentor context
  const isMentorContext = !!mentorProfileData

  // Get API signatures for prescription images.
  // Use detectCurrentPortal() (URL-based, always available) as the primary gate so
  // signatures are correct from the very first render — before mentorProfileData loads.
  const emrApiData = useMemo(() => {
    const portal = detectCurrentPortal()
    const isMentor = portal === PORTAL_TYPES.MENTOR || isMentorContext

    if (isMentor) {
      const directApis = getPrescriptionApis({ portalType: PORTAL_TYPES.MENTOR })
      if (directApis?.get && directApis?.save && directApis?.upload) {
        return {
          getApiSignature: directApis.get,
          saveApiSignature: directApis.save,
          uploadImagesSignature: directApis.upload,
          dialogTitle: 'Upload Prescription Images',
        }
      }
      // Hardcoded fallback for mentor portal
      return {
        getApiSignature: '/api/secure/mentor/emr-reports/prescription/images/get-by-eventId',
        saveApiSignature: '/api/secure/mentor/emr-reports/prescription/images/save',
        uploadImagesSignature: '/api/secure/mentor/emr-reports/file/upload',
        dialogTitle: 'Upload Prescription Images',
      }
    }

    // Fallback for non-mentor contexts
    const apis = getPrescriptionApis()
    return {
      getApiSignature: apis?.get || '',
      saveApiSignature: apis?.save || '',
      uploadImagesSignature: apis?.upload || '',
      dialogTitle: 'Upload Prescription Images',
    }
  }, [isMentorContext])
  
  // Warn if API signatures are missing
  useEffect(() => {
    if (!emrApiData?.getApiSignature || !emrApiData?.saveApiSignature || !emrApiData?.uploadImagesSignature) {
      if (isMentorContext || !eventId) {
        console.error('PrescriptionDocumentsUploadSection: Missing API signatures!', {
          hasGet: !!emrApiData?.getApiSignature,
          hasSave: !!emrApiData?.saveApiSignature,
          hasUpload: !!emrApiData?.uploadImagesSignature,
          isMentorContext,
          portalType: PORTAL_TYPES.MENTOR,
          hasEventId: !!eventId,
        })
      }
    }
  }, [emrApiData, isMentorContext, eventId])

  const fetchImages = useCallback(async (forceRefresh = false) => {
    // Skip re-fetching if we already have fresh data from the API unless forced
    if (!forceRefresh && hasFetchedRef.current) {
      return
    }

    if (!eventId) {
      return
    }

    if (!emrApiData?.getApiSignature) {
      if (process.env.NODE_ENV === 'development' || eventId) {
        console.error('PrescriptionDocumentsUploadSection: No API signature available', { 
          emrApiData,
          eventId,
        })
      }
      if (eventId) {
        setError('Unable to fetch images: API configuration not available. Please refresh the page.')
      }
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const resp = await getDataWithParams(emrApiData.getApiSignature, {
        eventId,
      })

      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp)
        
        const imageIds = data?.reportImages ?? data?.prescriptionImages ?? data?.uploadedImages ?? data?.fileIds ?? []
        const urls = data?.signedURLs ?? data?.signedUrls ?? data?.uploadedSignedImages ?? []
        
        let finalUrls = urls
        if (urls.length === 0 && imageIds.length > 0) {
          finalUrls = imageIds.map(id => getMediaUrl(id))
        }
        
        const uniqueImageIds = [...new Set(imageIds)]
        const uniqueUrls = [...new Set(finalUrls)]
        setImages(uniqueImageIds)
        setSignedUrls(uniqueUrls)
        // Mark as fetched so we don't repeat the API call on every context re-render
        hasFetchedRef.current = true
      } else {
        const errorMsg = resp?.data?.message || 'Failed to load images'
        console.error('PrescriptionDocumentsUploadSection: API error', {
          status: resp?.status,
          message: errorMsg,
          response: resp?.data,
        })
        setError(errorMsg)
      }
    } catch (err) {
      console.error('PrescriptionDocumentsUploadSection: Exception fetching images', err)
      setError(err?.message || 'Failed to load images')
    } finally {
      setLoading(false)
    }
  }, [eventId, emrApiData?.getApiSignature])

  // Sync images from prescriptionData whenever it changes
  useEffect(() => {
    if (prescriptionData) {
      const hasImages = prescriptionData.uploadedImages && prescriptionData.uploadedImages.length > 0
      const hasSignedUrls = prescriptionData.uploadedSignedImages && prescriptionData.uploadedSignedImages.length > 0
      
      if (hasSignedUrls) {
        const urls = prescriptionData.uploadedSignedImages.map(img => getMediaUrl(img))
        // Deduplicate to prevent duplicate key warnings
        setImages([...new Set(prescriptionData.uploadedImages || [])])
        setSignedUrls([...new Set(urls)])
      } 
      else if (hasImages) {
        const urls = prescriptionData.uploadedImages.map(id => getMediaUrl(id))
        // Deduplicate to prevent duplicate key warnings
        setImages([...new Set(prescriptionData.uploadedImages)])
        setSignedUrls([...new Set(urls)])
      }
    }
  }, [prescriptionData?.uploadedImages, prescriptionData?.uploadedSignedImages])

  // Fetch images on mount and when eventId changes.
  // Reset hasFetchedRef when eventId changes so a new session triggers a fresh fetch.
  useEffect(() => {
    hasFetchedRef.current = false
    if (eventId) {
      fetchImages()
    }
  }, [eventId, fetchImages])

  const openDeleteConfirmDialog = (index, url) => {
    setDeleteConfirmDialog({ open: true, index, url })
  }

  const closeDeleteConfirmDialog = () => {
    setDeleteConfirmDialog({ open: false, index: null, url: null })
  }

  const confirmDelete = async () => {
    const { index } = deleteConfirmDialog
    if (index !== null) {
      await handleDeleteImage(index)
    }
    closeDeleteConfirmDialog()
  }

  const openDocumentEditor = (index) => {
    const url = signedUrls[index]
    if (url) {
      setDocumentEditorState({ open: true, index, url })
    }
  }

  const closeDocumentEditor = () => {
    setDocumentEditorState({ open: false, index: null, url: null })
  }

  const handleDeleteImage = async (index) => {
    setDeletingIndex(index)
    
    try {
      const imageIdToDelete = images[index]
      const updatedImages = images.filter((_, i) => i !== index)
      const updatedSignedUrls = signedUrls.filter((_, i) => i !== index)

      // Save updated images list via API
      if (emrApiData?.saveApiSignature && eventId) {
        const resp = await postMPDataWithParams(emrApiData.saveApiSignature, {
          eventId,
          reportImages: updatedImages,
        })
        
        if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          setImages(updatedImages)
          setSignedUrls(updatedSignedUrls)
          toast.success('Document removed successfully')
          
          // Sync with prescription context if available
          if (setPrescriptionData) {
            setPrescriptionData((prev) => ({
              ...prev,
              prescriptionData: {
                ...(prev?.prescriptionData || {}),
                uploadedImages: updatedImages,
                uploadedSignedImages: updatedSignedUrls,
              },
            }))
          }
          
          if (onImagesUpdate) {
            onImagesUpdate(updatedImages)
          }
        } else {
          throw new Error(resp?.data?.message || 'Failed to delete document')
        }
      } else {
        // If no API, just update local state
        setImages(updatedImages)
        setSignedUrls(updatedSignedUrls)
        toast.success('Document removed')
        
        // Sync with prescription context if available
        if (setPrescriptionData) {
          setPrescriptionData((prev) => ({
            ...prev,
            prescriptionData: {
              ...(prev?.prescriptionData || {}),
              uploadedImages: updatedImages,
              uploadedSignedImages: updatedSignedUrls,
            },
          }))
        }
        
        if (onImagesUpdate) {
          onImagesUpdate(updatedImages)
        }
      }
    } catch (err) {
      console.error('PrescriptionDocumentsUploadSection: Error deleting image', err)
      toast.error(err?.message || 'Failed to delete document')
    } finally {
      setDeletingIndex(null)
    }
  }

  const imageCount = images?.length || 0

  const isPrintable = section?.printable
  const hasDocuments = imageCount > 0

  return (
    <>
      <Card
        sx={{
          mb: 3,
          maxWidth: '1200px',
          width: '100%',
          background: 'linear-gradient(135deg, rgba(250, 250, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%)',
          border: '1px solid rgba(67, 56, 202, 0.12)',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(67, 56, 202, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
          overflow: 'visible',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, rgba(67, 56, 202, 0.8) 0%, rgba(99, 102, 241, 0.8) 100%)',
            borderRadius: '16px 16px 0 0',
          },
        }}
        className={isPrintable ? '' : 'hide-on-print'}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          {/* Header Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: { xs: 2, md: 2.5 },
              pb: 2,
              borderBottom: '2px solid rgba(67, 56, 202, 0.1)',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: { xs: '36px', md: '40px' },
                  height: { xs: '36px', md: '40px' },
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                  color: '#4338ca',
                  flexShrink: 0,
                }}
              >
                <AttachFileIcon sx={{ fontSize: { xs: 20, md: 22 } }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: { xs: '18px', md: '20px' },
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    letterSpacing: '-0.01em',
                    mb: 0.5,
                  }}
                >
                  Uploaded Documents
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748b',
                    fontSize: '12px',
                    fontStyle: 'italic',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {loading ? 'Loading...' : `${imageCount} document${imageCount !== 1 ? 's' : ''} attached`}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {isPrintable && (
                <Tooltip title="Included in printed prescription" arrow placement="top">
                  <Chip
                    icon={<Print sx={{ fontSize: '16px' }} />}
                    label="On Rx"
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      color: '#059669',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      fontWeight: 600,
                      fontSize: '11px',
                      height: '24px',
                      '& .MuiChip-icon': {
                        color: '#059669',
                      },
                    }}
                  />
                </Tooltip>
              )}
              <Button
                variant="contained"
                startIcon={<ImageIcon sx={{ fontSize: '18px' }} />}
                onClick={(e) => {
                  e.preventDefault()
                  if (uploadDialogOpen) {
                    return
                  }
                  setUploadDialogOpen(true)
                }}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
                  boxShadow: '0 4px 12px rgba(67, 56, 202, 0.35)',
                  color: '#fff',
                  letterSpacing: '0.2px',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #3730a3 0%, #4f46e5 100%)',
                    boxShadow: '0 6px 16px rgba(67, 56, 202, 0.45)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: '0 2px 8px rgba(67, 56, 202, 0.35)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {hasDocuments ? 'Upload More' : 'Upload Documents'}
              </Button>
            </Box>
          </Box>

          {/* Content Section */}
          <Box>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} sx={{ color: '#10b981' }} />
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {/* Thumbnail preview when collapsed */}
            {!expanded && imageCount > 0 && !loading && (
            <ThumbnailContainer>
              {signedUrls.slice(0, 6).map((url, index) => (
                <ThumbnailWrapper key={index}>
                  <Tooltip title="Delete document">
                    <ThumbnailDeleteButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        openDeleteConfirmDialog(index, url)
                      }}
                      disabled={deletingIndex === index}
                    >
                      {deletingIndex === index ? (
                        <CircularProgress size={10} sx={{ color: 'white' }} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </ThumbnailDeleteButton>
                  </Tooltip>
                  <Thumbnail onClick={() => setExpanded(true)}>
                    <img 
                      src={url} 
                      alt={`Document ${index + 1}`}
                      onError={(e) => {
                        console.error('PrescriptionDocumentsUploadSection: Thumbnail load error', {
                          url,
                          index,
                        })
                        e.target.style.display = 'none'
                      }}
                    />
                  </Thumbnail>
                </ThumbnailWrapper>
              ))}
              {signedUrls.length > 6 && (
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '8px',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    border: '2px dashed rgba(16, 185, 129, 0.3)',
                  }}
                  onClick={() => setExpanded(true)}
                >
                  <Typography sx={{ color: '#10b981', fontWeight: 600, fontSize: '12px' }}>
                    +{signedUrls.length - 6}
                  </Typography>
                </Box>
              )}
            </ThumbnailContainer>
          )}

            {/* Expanded view */}
            <Collapse in={expanded}>
            <Box sx={{ mt: 2 }}>
              {/* Thumbnail navigation */}
              <ThumbnailContainer sx={{ mb: 2, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                {signedUrls.map((url, index) => (
                  <ThumbnailWrapper key={index}>
                    <Tooltip title="Delete document">
                      <ThumbnailDeleteButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          openDeleteConfirmDialog(index, url)
                        }}
                        disabled={deletingIndex === index}
                      >
                        {deletingIndex === index ? (
                          <CircularProgress size={10} sx={{ color: 'white' }} />
                        ) : (
                          <DeleteIcon />
                        )}
                      </ThumbnailDeleteButton>
                    </Tooltip>
                    <Thumbnail onClick={() => openDocumentEditor(index)}>
                      <img 
                        src={url} 
                        alt={`Document ${index + 1}`}
                        onError={(e) => {
                          console.error('PrescriptionDocumentsUploadSection: Image load error', {
                            url,
                            index,
                            error: e,
                          })
                          e.target.style.display = 'none'
                        }}
                      />
                    </Thumbnail>
                  </ThumbnailWrapper>
                ))}
              </ThumbnailContainer>

              {/* Full-size images */}
              {signedUrls.length > 0 ? (
                signedUrls.map((url, index) => (
                  <ImageItem key={index}>
                    <DeleteOverlay>
                      <Tooltip title="Edit document">
                        <IconButton
                          size="small"
                          onClick={() => openDocumentEditor(index)}
                          sx={{
                            backgroundColor: 'rgba(16, 185, 129, 0.9)',
                            color: 'white',
                            mr: 1,
                            '&:hover': {
                              backgroundColor: 'rgba(16, 185, 129, 1)',
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete document">
                        <IconButton
                          size="small"
                          onClick={() => openDeleteConfirmDialog(index, url)}
                          disabled={deletingIndex === index}
                          sx={{
                            backgroundColor: 'rgba(239, 68, 68, 0.9)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(239, 68, 68, 1)',
                            },
                          }}
                        >
                          {deletingIndex === index ? (
                            <CircularProgress size={16} sx={{ color: 'white' }} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </DeleteOverlay>
                    <img 
                      src={url} 
                      alt={`Document ${index + 1}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => openDocumentEditor(index)}
                      onError={(e) => {
                        console.error('PrescriptionDocumentsUploadSection: Full image load error', {
                          url,
                          index,
                          error: e,
                        })
                        e.target.style.display = 'none'
                      }}
                    />
                  </ImageItem>
                ))
              ) : null}
            </Box>
          </Collapse>
          </Box>
        </CardContent>
      </Card>

      {/* Upload Dialog - Always rendered, controlled by open prop */}
      <EmrImagesUploadDialog
        open={uploadDialogOpen}
        bookedEventInfo={bookedEventInfo || { id: eventId, eventType: 'prescription' }}
        closePopover={() => {
          setUploadDialogOpen(false)
        }}
        onSuccess={(savedImageIds) => {
          // Immediately refresh images when save is successful (force refresh to bypass cache)
          fetchImages(true) // forceRefresh = true to bypass early return
        }}
        showGallery={false}
        fileUploadApiSignature={emrApiData?.uploadImagesSignature || ''}
        emrImagesGetApiSignature={emrApiData?.getApiSignature || ''}
        emrImagesSaveApiSignature={emrApiData?.saveApiSignature || ''}
        dialogTitle={emrApiData?.dialogTitle || "Upload Prescription Documents"}
        initialImages={images}
        initialSignedUrls={signedUrls}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={closeDeleteConfirmDialog}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxWidth: '360px',
            width: '100%',
          },
        }}
      >
        <IconButton
          onClick={closeDeleteConfirmDialog}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#9ca3af',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DeleteDialogContent>
          <DeleteDialogIcon>
            <WarningAmberIcon />
          </DeleteDialogIcon>
          <DeleteDialogTitle>Delete Document?</DeleteDialogTitle>
          <DeleteDialogDescription>
            This document will be permanently removed from this prescription. This action cannot be undone.
          </DeleteDialogDescription>
          <DeleteDialogActions>
            <CancelButton onClick={closeDeleteConfirmDialog}>
              Cancel
            </CancelButton>
            <DeleteButton 
              onClick={confirmDelete}
              disabled={deletingIndex !== null}
            >
              {deletingIndex !== null ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : (
                'Delete'
              )}
            </DeleteButton>
          </DeleteDialogActions>
        </DeleteDialogContent>
      </Dialog>

      {/* Document Editor Dialog */}
      <DocumentEditorDialog
        open={documentEditorState.open}
        onClose={closeDocumentEditor}
        imageUrl={documentEditorState.url}
        title={`Edit Document ${documentEditorState.index !== null ? documentEditorState.index + 1 : ''}`}
      />
    </>
  )
}

